import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Settings as SettingsIcon, Sparkles } from 'lucide-react';
import { Crown, Home as HomeIcon, Flag, BookOpen, Rocket } from 'lucide-react';
import SettingsModal from './SettingsModal';

function Header({ breadcrumbs = [] }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 border-b border-cyan-400/20 backdrop-blur-md bg-card/80">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/explore/octants')} data-testid="header-logo">
              <img 
                src="/logo.png" 
                alt="Rook Games" 
                className="h-10 w-10 object-contain"
              />
              <h1 className="text-2xl font-bold text-primary" data-testid="app-title">KRAKEN</h1>
            </div>
            
            {/* Navigation Links */}
            <nav className="hidden lg:flex items-center space-x-4">
              <button
                onClick={() => navigate('/explore/octants')}
                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="nav-explore"
              >
                Explore
              </button>
              <button
                onClick={() => navigate('/elements')}
                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="nav-elements"
              >
                Elements
              </button>
              <button
                onClick={() => navigate('/inventory')}
                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="nav-inventory"
              >
                Inventory
              </button>
              <button
                onClick={() => navigate('/missions')}
                className="text-sm text-amber-300 hover:text-amber-200 transition-colors flex items-center gap-1.5"
                data-testid="nav-missions"
              >
                <Sparkles className="w-4 h-4" />
                Missions
              </button>
              <button
                onClick={() => navigate('/discoveries')}
                className="text-sm text-yellow-200 hover:text-yellow-100 transition-colors flex items-center gap-1.5"
                data-testid="nav-discoveries"
              >
                <Crown className="w-4 h-4" />
                Discoveries
              </button>
              <button
                onClick={() => navigate('/story')}
                className="text-sm text-orange-200 hover:text-orange-100 transition-colors flex items-center gap-1.5"
                data-testid="nav-story"
              >
                <BookOpen className="w-4 h-4" />
                Campaign
              </button>
              <button
                onClick={() => navigate('/factions')}
                className="text-sm text-rose-200 hover:text-rose-100 transition-colors flex items-center gap-1.5"
                data-testid="nav-factions"
              >
                <Flag className="w-4 h-4" />
                Factions
              </button>
              <button
                onClick={() => navigate('/fleet')}
                className="text-sm text-sky-200 hover:text-sky-100 transition-colors flex items-center gap-1.5"
                data-testid="nav-fleet"
              >
                <Rocket className="w-4 h-4" />
                Fleet
              </button>
              <button
                onClick={() => navigate('/base')}
                className="text-sm text-cyan-200 hover:text-cyan-100 transition-colors flex items-center gap-1.5"
                data-testid="nav-base"
              >
                <HomeIcon className="w-4 h-4" />
                Base
              </button>
              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors font-semibold"
                  data-testid="nav-admin"
                >
                  Admin
                </button>
              )}
            </nav>
            
            {breadcrumbs.length > 0 && (
              <nav className="hidden md:flex items-center space-x-2 text-sm" data-testid="breadcrumbs">
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <span className="text-muted-foreground">→</span>}
                    {crumb.link ? (
                      <button
                        onClick={() => navigate(crumb.link)}
                        className="text-primary hover:underline"
                        data-testid={`breadcrumb-${index}`}
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span className="text-foreground font-medium" data-testid={`breadcrumb-${index}`}>{crumb.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 px-4 py-2 bg-white/5 rounded-lg border border-cyan-400/20" data-testid="credits-display">
              <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
              <span className="font-mono font-semibold text-lg">{user?.credits || 0}</span>
            </div>

            <button
              onClick={() => setSettingsOpen(true)}
              className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-cyan-400/20 rounded-lg transition-all text-sm flex items-center gap-2"
              data-testid="header-settings-button"
              aria-label="Open settings"
            >
              <SettingsIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
            </button>

            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-cyan-400/20 rounded-lg transition-all text-sm"
              data-testid="logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </header>
  );
}

export default Header;
